<template>
  <div class="calc-container">
    <!-- 输入卡片 -->
    <div class="card">
      <div class="title">
        <h1>电商投产对比计算器</h1>
      </div>

      <div class="item">
        <div class="label">单件成本（元）</div>
        <input
          v-model.number="form.cost"
          type="number"
          step="0.01"
          @input="calc"
          class="input"
        />
      </div>
      <div class="item">
        <div class="label">销售单价（元）</div>
        <input
          v-model.number="form.price"
          type="number"
          step="0.01"
          @input="calc"
          class="input"
        />
      </div>
      <div class="item">
        <div class="label">退货率（%）</div>
        <input
          v-model.number="form.returnRate"
          type="number"
          step="0.1"
          @input="calc"
          class="input"
        />
      </div>
      <div class="item">
        <div class="label">平台服务费（%）</div>
        <input
          v-model.number="form.serviceRate"
          type="number"
          step="0.01"
          @input="calc"
          class="input"
        />
      </div>
    </div>

    <!-- 结果展示卡片 -->
    <div class="card">
      <div class="grid">
        <div class="col">
          <div class="col-title">未扣退款&服务费</div>
          <div class="row">
            <div class="label-center">毛利</div>
            <div :class="getClass(result.oriProfitVal)">{{ result.oriProfit }}</div>
          </div>
          <div class="row">
            <div class="label-center">毛利率</div>
            <div :class="getClass(result.oriProfitVal)">{{ result.oriMargin }}</div>
          </div>
          <div class="row">
            <div class="label-center">投产ROI</div>
            <div :class="getClass(result.oriProfitVal)">{{ result.oriRoi }}</div>
          </div>
        </div>

        <div class="col">
          <div class="col-title">已扣退款+服务费</div>
          <div class="row">
            <div class="label-center">真实毛利</div>
            <div :class="getClass(result.realProfitVal)">{{ result.realProfit }}</div>
          </div>
          <div class="row">
            <div class="label-center">真实毛利率</div>
            <div :class="getClass(result.realProfitVal)">{{ result.realMargin }}</div>
          </div>
          <div class="row">
            <div class="label-center">真实投产ROI</div>
            <div :class="getClass(result.realProfitVal)">{{ result.realRoi }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 说明文字 -->
    <div class="tip">
      真实投产ROI = 1 ÷ 利润率 ÷ (1−退货率)<br>
      已自动包含：平台0.06%技术服务费
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'

// 表单数据（响应式）
const form = reactive({
  cost: 10.43,
  price: 14.90,
  returnRate: 0,
  serviceRate: 0.06
})

// 计算结果
const result = ref({
  oriProfitVal: 0,
  oriProfit: '-',
  oriMargin: '-',
  oriRoi: '-',

  realProfitVal: 0,
  realProfit: '-',
  realMargin: '-',
  realRoi: '-'
})

// 核心计算函数
const calc = () => {
  const { cost, price, returnRate, serviceRate } = form
  const rr = returnRate / 100
  const sr = serviceRate / 100

  // 左侧：未扣费用
  const oriProfitVal = price - cost
  const oriProfit = oriProfitVal.toFixed(2)
  const oriMarginVal = price !== 0 ? oriProfitVal / price : 0
  const oriMargin = (oriMarginVal * 100).toFixed(1) + '%'
  const oriRoi = oriMarginVal !== 0 ? (1 / oriMarginVal).toFixed(2) : '-'

  // 右侧：真实利润
  const serviceFee = price * sr
  const realProfitVal = price - cost - serviceFee
  const realProfit = realProfitVal.toFixed(2)
  const realMarginVal = price !== 0 ? realProfitVal / price : 0
  const realMargin = (realMarginVal * 100).toFixed(1) + '%'

  let realRoi = '-'
  if (realMarginVal !== 0) {
    const roiCalc = 1 / realMarginVal / (1 - rr)
    realRoi = roiCalc.toFixed(2)
  }

  // 更新结果
  result.value = {
    oriProfitVal,
    oriProfit,
    oriMargin,
    oriRoi,
    realProfitVal,
    realProfit,
    realMargin,
    realRoi
  }
}

// 文字颜色样式
const getClass = (val) => {
  return val < 0 ? 'val red' : 'val'
}

// 初始化计算
onMounted(() => {
  calc()
})
</script>

<style scoped>
*{margin:0;padding:0;box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif}
.calc-container{background:#f2f4f7;padding:20px 15px;min-height:100vh}
.card{background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,0.06);margin-bottom:12px}
.title{background:#3377ff;color:#fff;padding:16px;text-align:center}
.title h1{font-size:18px}
.item{display:flex;justify-content:space-between;align-items:center;padding:14px 18px;border-bottom:1px solid #f0f2f5}
.label{font-size:15px;color:#333}
.label-center{text-align:center;font-size:14px;color:#333}
.input{width:110px;text-align:right;border:1px solid #e5e7eb;border-radius:8px;padding:8px;font-size:15px;outline:none}
.input:focus{border-color:#3377ff}
.grid{display:grid;grid-template-columns:1fr 1fr}
.col{padding:12px;border-right:1px solid #f0f2f5}
.col:last-child{border-right:none}
.col-title{text-align:center;font-size:14px;color:#666;margin-bottom:8px}
.val{font-size:16px;font-weight:600;color:#3377ff;text-align:center}
.val.red{color:#ff4d4f}
.row{padding:10px 0}
.tip{font-size:12px;color:#888;text-align:center;line-height:1.4}
</style>